import React, { useState, useEffect, useCallback } from 'react';
import useSimulatorAPI from '../hooks/useSimulatorAPI';
import './ToastSimulator.css';

const ToastSimulator = () => {
  const sim = useSimulatorAPI();
  const [activeTab, setActiveTab] = useState('control');
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [simDate, setSimDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [params, setParams] = useState(null);
  const [browseTab, setBrowseTab] = useState('orders');
  const [browseData, setBrowseData] = useState([]);
  const [browseDate, setBrowseDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [browseLoading, setBrowseLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [seedSections, setSeedSections] = useState({ restaurant: true, menu: true, employees: true, config: true });

  const addLog = useCallback((message, type = 'info') => {
    setLogs(prev => [...prev.slice(-50), { message, type, time: new Date().toLocaleTimeString() }]);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await sim.getStatus();
      setStatus(data);
      if (data.config) {
        setParams(data.config);
      }
    } catch (err) {
      addLog(`Failed to fetch status: ${err.message}`, 'error');
    }
  }, [sim, addLog]);

  // Initial fetch + auto-poll while an action is running
  useEffect(() => {
    fetchStatus();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!actionLoading) return;
    const interval = setInterval(async () => {
      try {
        const data = await sim.getStatus();
        setStatus(data);
        if (data.config) setParams(data.config);

        const seed = data?.seed_status;
        // Seed finished
        if (actionLoading === 'seed' && seed?.seeded && !seed?.in_progress) {
          addLog(`Seed complete. ${seed.total_entities} entities created.`, 'success');
          setActionLoading(null);
        }
        // Daily finished — check if last_sim_date changed
        if (actionLoading === 'daily' && data?.last_sim_date?.date === pendingSimDate.current) {
          addLog(`Simulation complete for ${pendingSimDate.current}.`, 'success');
          setActionLoading(null);
        }
      } catch {
        // poll quietly
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [actionLoading]);  // eslint-disable-line react-hooks/exhaustive-deps

  const pendingSimDate = React.useRef(null);

  const handleSeed = async () => {
    const selected = Object.entries(seedSections).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) {
      addLog('Select at least one section to seed.', 'error');
      return;
    }
    const isAll = selected.length === 4;
    setActionLoading('seed');
    addLog(`Starting seed generation for ${isAll ? 'all sections' : selected.join(', ')} (runs in background)...`, 'info');
    try {
      await sim.seedRestaurant(isAll ? null : selected);
      addLog('Seed request accepted. Polling for completion...', 'info');
    } catch (err) {
      addLog(`Seed request failed: ${err.message}`, 'error');
      setActionLoading(null);
    }
  };

  const handleSimulateDay = async () => {
    const bd = simDate.replace(/-/g, '');
    pendingSimDate.current = bd;
    setActionLoading('daily');
    addLog(`Simulating business day ${simDate} (runs in background)...`, 'info');
    try {
      await sim.simulateDay(bd);
      addLog('Simulation request accepted. Polling for completion...', 'info');
    } catch (err) {
      addLog(`Simulation request failed: ${err.message}`, 'error');
      setActionLoading(null);
    }
  };

  const handleSimulateWeek = async () => {
    setActionLoading('week');
    addLog('Simulating 7 days...', 'info');
    const start = new Date(simDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const bd = d.toISOString().split('T')[0].replace(/-/g, '');
      addLog(`Day ${i + 1}/7: ${bd}`, 'info');
      try {
        await sim.simulateDay(bd);
        // Wait for each day to finish before starting next
        await _waitForSimDay(bd);
        addLog(`Day ${bd} complete`, 'success');
      } catch (err) {
        addLog(`Day ${bd} failed: ${err.message}`, 'error');
      }
    }
    addLog('Week simulation complete', 'success');
    await fetchStatus();
    setActionLoading(null);
  };

  const _waitForSimDay = async (bd) => {
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const data = await sim.getStatus();
        if (data?.sim_days?.[bd]) return;
      } catch { /* retry */ }
    }
  };

  const handleSaveParams = async () => {
    setActionLoading('params');
    try {
      await sim.updateParams(params);
      addLog('Parameters saved', 'success');
    } catch (err) {
      addLog(`Failed to save params: ${err.message}`, 'error');
    }
    setActionLoading(null);
  };

  const handleResetParams = () => {
    setParams(null);
    fetchStatus();
  };

  const handleBrowse = async () => {
    setBrowseLoading(true);
    setBrowseData([]);
    const bd = browseDate.replace(/-/g, '');
    try {
      let path;
      let queryParams = {};
      switch (browseTab) {
        case 'orders':
          path = '/orders/v2/ordersBulk';
          queryParams = { businessDate: bd };
          break;
        case 'employees':
          path = '/labor/v1/employees';
          break;
        case 'menuItems':
          path = '/config/v2/menuItems';
          break;
        case 'payments':
          path = '/orders/v2/payments';
          queryParams = { paidBusinessDate: bd };
          break;
        default:
          path = '/orders/v2/ordersBulk';
          queryParams = { businessDate: bd };
      }
      const data = await sim.callSimToastAPI(path, queryParams);
      setBrowseData(Array.isArray(data) ? data : [data]);
    } catch (err) {
      addLog(`Browse failed: ${err.message}`, 'error');
    }
    setBrowseLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'browse') {
      handleBrowse();
    }
  }, [browseTab, browseDate, activeTab]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleTestConnection = async () => {
    setTestResult(null);
    try {
      const result = await sim.callSimAPI('/authentication/v1/authentication/login', { method: 'POST', body: {} });
      setTestResult({ success: true, message: `Connected! Token: ${result?.token?.accessToken?.substring(0, 30)}...` });
    } catch (err) {
      setTestResult({ success: false, message: `Connection failed: ${err.message}` });
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(sim.simulatorUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const seedStatus = status?.seed_status;
  const isSeeded = seedStatus?.seeded;
  const simDays = status?.simulated_days || 0;

  const tabs = [
    { id: 'control', label: 'Control Panel' },
    { id: 'params', label: 'Parameters' },
    { id: 'browse', label: 'Browse Data' },
    { id: 'integration', label: 'Integration' },
  ];

  const updateParam = (path, value) => {
    setParams(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  return (
    <div className="simulator-page">
      <div className="simulator-header">
        <h1>Toast Simulator</h1>
        <span className={`sim-badge ${isSeeded ? 'seeded' : 'not-seeded'} ${actionLoading ? 'simulating' : ''}`}>
          <span className="sim-badge-dot" />
          {actionLoading ? 'Processing...' : isSeeded ? 'Seeded' : 'Not Seeded'}
        </span>
      </div>

      {/* Stats bar */}
      <div className="sim-stats-bar">
        <div className="sim-stat-card">
          <div className="sim-stat-label">Seeded Entities</div>
          <div className="sim-stat-value">{seedStatus?.total_entities || 0}</div>
        </div>
        <div className="sim-stat-card">
          <div className="sim-stat-label">Simulated Days</div>
          <div className="sim-stat-value">{simDays}</div>
        </div>
        <div className="sim-stat-card">
          <div className="sim-stat-label">Last Simulation</div>
          <div className="sim-stat-value" style={{ fontSize: 16 }}>
            {status?.last_sim_date?.date
              ? `${status.last_sim_date.date.substring(0,4)}-${status.last_sim_date.date.substring(4,6)}-${status.last_sim_date.date.substring(6,8)}`
              : 'Never'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sim-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`sim-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="sim-tab-content">
        {activeTab === 'control' && (
          <ControlPanel
            isSeeded={isSeeded}
            actionLoading={actionLoading}
            simDate={simDate}
            setSimDate={setSimDate}
            onSeed={handleSeed}
            onSimulateDay={handleSimulateDay}
            onSimulateWeek={handleSimulateWeek}
            seedStatus={seedStatus}
            status={status}
            logs={logs}
            seedSections={seedSections}
            setSeedSections={setSeedSections}
          />
        )}

        {activeTab === 'params' && params && (
          <ParamsTab
            params={params}
            updateParam={updateParam}
            onSave={handleSaveParams}
            onReset={handleResetParams}
            actionLoading={actionLoading}
          />
        )}

        {activeTab === 'browse' && (
          <BrowseTab
            browseTab={browseTab}
            setBrowseTab={setBrowseTab}
            browseDate={browseDate}
            setBrowseDate={setBrowseDate}
            browseData={browseData}
            browseLoading={browseLoading}
            onRefresh={handleBrowse}
          />
        )}

        {activeTab === 'integration' && (
          <IntegrationTab
            simulatorUrl={sim.simulatorUrl}
            onTest={handleTestConnection}
            testResult={testResult}
            onCopy={handleCopyUrl}
            copied={copied}
          />
        )}
      </div>
    </div>
  );
};

// ===== Control Panel Tab =====
const SEED_SECTION_LABELS = {
  restaurant: 'Restaurant',
  menu: 'Menu',
  employees: 'Employees',
  config: 'Config',
};

const ControlPanel = ({ isSeeded, actionLoading, simDate, setSimDate, onSeed, onSimulateDay, onSimulateWeek, seedStatus, status, logs, seedSections, setSeedSections }) => (
  <>
    <div className="sim-controls">
      <div className="sim-control-card">
        <h3>Seed Restaurant</h3>
        <p>Generate restaurant data using AI. Uncheck sections to preserve existing data.</p>
        <div className="sim-seed-sections">
          {Object.entries(SEED_SECTION_LABELS).map(([key, label]) => (
            <label key={key} className="sim-seed-section-label">
              <input
                type="checkbox"
                checked={seedSections[key]}
                onChange={e => setSeedSections(prev => ({ ...prev, [key]: e.target.checked }))}
                disabled={!!actionLoading}
              />
              {label}
            </label>
          ))}
        </div>
        <button className="sim-btn sim-btn-primary" onClick={onSeed} disabled={!!actionLoading}>
          {actionLoading === 'seed' ? <><span className="sim-spinner" /> Seeding...</> : 'Seed Restaurant'}
        </button>
        {seedStatus?.seeded_at && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            Last seeded: {new Date(seedStatus.seeded_at).toLocaleString()}
            {seedStatus.sections && seedStatus.sections.length < 4 && (
              <> (sections: {seedStatus.sections.join(', ')})</>
            )}
          </p>
        )}
      </div>

      <div className="sim-control-card">
        <h3>Simulate Business Day</h3>
        <p>Generate orders, shifts, payments, and cash activity for a specific date.</p>
        <div>
          <input
            type="date"
            className="sim-date-input"
            value={simDate}
            onChange={e => setSimDate(e.target.value)}
          />
          <button
            className="sim-btn sim-btn-success"
            onClick={onSimulateDay}
            disabled={!isSeeded || !!actionLoading}
          >
            {actionLoading === 'daily' ? <><span className="sim-spinner" /> Running...</> : 'Run Simulation'}
          </button>
        </div>
        <div className="sim-quick-actions">
          <button
            className="sim-btn sim-btn-secondary"
            onClick={onSimulateWeek}
            disabled={!isSeeded || !!actionLoading}
          >
            {actionLoading === 'week' ? 'Running...' : 'Simulate This Week'}
          </button>
        </div>
      </div>
    </div>

    {/* Status */}
    {seedStatus?.entity_counts && (
      <div className="sim-status-card" style={{ marginTop: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Entity Counts</h3>
        {Object.entries(seedStatus.entity_counts).map(([key, val]) => (
          <div className="sim-status-row" key={key}>
            <span className="sim-status-label">{key.replace(/_/g, ' ')}</span>
            <span className="sim-status-value">{val}</span>
          </div>
        ))}
      </div>
    )}

    {/* Logs */}
    {logs.length > 0 && (
      <div className="sim-log">
        {logs.map((log, i) => (
          <div key={i} className={`sim-log-entry ${log.type}`}>
            [{log.time}] {log.message}
          </div>
        ))}
      </div>
    )}
  </>
);

// ===== Parameters Tab =====
const ParamsTab = ({ params, updateParam, onSave, onReset, actionLoading }) => (
  <>
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      <button className="sim-btn sim-btn-primary" onClick={onSave} disabled={!!actionLoading}>
        {actionLoading === 'params' ? 'Saving...' : 'Save Parameters'}
      </button>
      <button className="sim-btn sim-btn-secondary" onClick={onReset}>
        Reset to Defaults
      </button>
    </div>
    <div className="sim-params-form">
      <div className="sim-param-section">
        <h4>Restaurant</h4>
        <ParamRow label="Name" value={params.restaurant_name} onChange={v => updateParam('restaurant_name', v)} type="text" />
        <ParamRow label="Timezone" value={params.timezone} onChange={v => updateParam('timezone', v)} type="text" />
        <ParamRow label="Menu Items" value={params.num_menu_items} onChange={v => updateParam('num_menu_items', parseInt(v) || 0)} />
        <ParamRow label="Menu Groups" value={params.num_menu_groups} onChange={v => updateParam('num_menu_groups', parseInt(v) || 0)} />
        <ParamRow label="Employees" value={params.num_employees} onChange={v => updateParam('num_employees', parseInt(v) || 0)} />
      </div>

      <div className="sim-param-section">
        <h4>Orders</h4>
        <ParamRow label="Min Orders/Day" value={params.orders_per_day?.min} onChange={v => updateParam('orders_per_day.min', parseInt(v) || 0)} />
        <ParamRow label="Max Orders/Day" value={params.orders_per_day?.max} onChange={v => updateParam('orders_per_day.max', parseInt(v) || 0)} />
        <ParamRow label="Min Items/Order" value={params.avg_items_per_order?.min} onChange={v => updateParam('avg_items_per_order.min', parseInt(v) || 0)} />
        <ParamRow label="Max Items/Order" value={params.avg_items_per_order?.max} onChange={v => updateParam('avg_items_per_order.max', parseInt(v) || 0)} />
        <ParamRow label="Tax Rate" value={params.tax_rate} onChange={v => updateParam('tax_rate', parseFloat(v) || 0)} step="0.0001" />
        <ParamRow label="Dine-In Min (min)" value={params.dine_in_duration?.min} onChange={v => updateParam('dine_in_duration.min', parseInt(v) || 0)} />
        <ParamRow label="Dine-In Max (min)" value={params.dine_in_duration?.max} onChange={v => updateParam('dine_in_duration.max', parseInt(v) || 0)} />
        <ParamRow label="Takeout Min (min)" value={params.takeout_duration?.min} onChange={v => updateParam('takeout_duration.min', parseInt(v) || 0)} />
        <ParamRow label="Takeout Max (min)" value={params.takeout_duration?.max} onChange={v => updateParam('takeout_duration.max', parseInt(v) || 0)} />
        <ParamRow label="Reservation %" value={params.reservation_ratio} onChange={v => updateParam('reservation_ratio', parseFloat(v) || 0)} step="0.01" />
      </div>

      <div className="sim-param-section">
        <h4>Payments & Tips</h4>
        <ParamRow label="Credit %" value={params.payment_type_distribution?.CREDIT} onChange={v => updateParam('payment_type_distribution.CREDIT', parseFloat(v) || 0)} step="0.01" />
        <ParamRow label="Cash %" value={params.payment_type_distribution?.CASH} onChange={v => updateParam('payment_type_distribution.CASH', parseFloat(v) || 0)} step="0.01" />
        <ParamRow label="Avg Tip %" value={params.tip_percentage?.avg} onChange={v => updateParam('tip_percentage.avg', parseFloat(v) || 0)} step="0.01" />
        <ParamRow label="Min Tip %" value={params.tip_percentage?.min} onChange={v => updateParam('tip_percentage.min', parseFloat(v) || 0)} step="0.01" />
        <ParamRow label="Max Tip %" value={params.tip_percentage?.max} onChange={v => updateParam('tip_percentage.max', parseFloat(v) || 0)} step="0.01" />
      </div>

      <div className="sim-param-section" style={{ gridColumn: '1 / -1' }}>
        <h4>Covers (Party Size Distribution)</h4>
        <table className="sim-config-table">
          <thead><tr><th>Party Size</th><th>Weight (Dine-in)</th></tr></thead>
          <tbody>
            {['1', '2', '3', '4', '5', '6'].map(size => (
              <tr key={size}>
                <td>{size}-top</td>
                <td><input type="number" className="sim-table-input" value={params.covers_per_order?.dine_in?.[size] ?? 0} onChange={e => updateParam(`covers_per_order.dine_in.${size}`, parseInt(e.target.value) || 0)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
          <ParamRow label="Takeout Min" value={params.covers_per_order?.other?.min} onChange={v => updateParam('covers_per_order.other.min', parseInt(v) || 1)} />
          <ParamRow label="Takeout Max" value={params.covers_per_order?.other?.max} onChange={v => updateParam('covers_per_order.other.max', parseInt(v) || 1)} />
        </div>
      </div>

      <div className="sim-param-section" style={{ gridColumn: '1 / -1' }}>
        <h4>Max Covers per Position</h4>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>Max concurrent covers each position can handle. Set to 0 for non-serving positions.</p>
        <table className="sim-config-table">
          <thead><tr><th>Position</th><th>Max Covers</th></tr></thead>
          <tbody>
            {Object.entries(params.max_covers_per_position || {}).map(([pos, val]) => (
              <tr key={pos}>
                <td>{pos}</td>
                <td><input type="number" className="sim-table-input" value={val} onChange={e => updateParam(`max_covers_per_position.${pos}`, parseInt(e.target.value) || 0)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sim-param-section" style={{ gridColumn: '1 / -1' }}>
        <h4>Tables</h4>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>
          Total: {(params.tables || []).reduce((s, t) => s + t.count, 0)} tables, {(params.tables || []).reduce((s, t) => s + t.size * t.count, 0)} covers
        </p>
        <table className="sim-config-table">
          <thead><tr><th>Size</th><th>Type</th><th>Count</th><th>Covers</th><th></th></tr></thead>
          <tbody>
            {(params.tables || []).map((t, i) => (
              <tr key={i}>
                <td>
                  <input type="number" className="sim-table-input" min="1" value={t.size}
                    onChange={e => { const u = JSON.parse(JSON.stringify(params.tables)); u[i].size = parseInt(e.target.value) || 1; updateParam('tables', u); }} />
                </td>
                <td>
                  <select className="sim-table-select" value={t.type} onChange={e => { const u = JSON.parse(JSON.stringify(params.tables)); u[i].type = e.target.value; updateParam('tables', u); }}>
                    <option value="regular">Regular</option>
                    <option value="booth">Booth</option>
                    <option value="communal">Communal</option>
                    <option value="bar">Bar</option>
                    <option value="patio">Patio</option>
                    <option value="high-top">High Top</option>
                  </select>
                </td>
                <td>
                  <input type="number" className="sim-table-input" min="0" value={t.count}
                    onChange={e => { const u = JSON.parse(JSON.stringify(params.tables)); u[i].count = parseInt(e.target.value) || 0; updateParam('tables', u); }} />
                </td>
                <td style={{ color: '#64748b' }}>{t.size * t.count}</td>
                <td>
                  <button className="sim-table-remove" onClick={() => { const u = params.tables.filter((_, j) => j !== i); updateParam('tables', u); }}>&times;</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="sim-btn sim-btn-secondary" style={{ marginTop: 8, fontSize: 12, padding: '4px 12px' }}
          onClick={() => { const u = [...(params.tables || []), { size: 4, type: 'regular', count: 1 }]; updateParam('tables', u); }}>
          + Add Row
        </button>
      </div>

      <div className="sim-param-section">
        <h4>Labor</h4>
        <ParamRow label="Open Hour" value={params.operating_hours?.open} onChange={v => updateParam('operating_hours.open', parseInt(v) || 0)} />
        <ParamRow label="Close Hour" value={params.operating_hours?.close} onChange={v => updateParam('operating_hours.close', parseInt(v) || 0)} />
        <ParamRow label="Break Probability" value={params.break_probability} onChange={v => updateParam('break_probability', parseFloat(v) || 0)} step="0.01" />
        <ParamRow label="Auto Clock-Out %" value={params.auto_clock_out_probability} onChange={v => updateParam('auto_clock_out_probability', parseFloat(v) || 0)} step="0.01" />
      </div>
    </div>
    <div className="sim-param-section" style={{ marginTop: 20, gridColumn: '1 / -1' }}>
      <h4>AI Steering Prompt</h4>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px' }}>
        Additional instructions passed to the AI when generating menu items, employee names, and config. Applied on next seed.
      </p>
      <textarea
        style={{ width: '100%', minHeight: 80, padding: 8, border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical' }}
        value={params.steering_prompt || ''}
        onChange={e => updateParam('steering_prompt', e.target.value)}
        placeholder="e.g., Focus on street food style items. Use Oaxacan cuisine specifically. Include more vegetarian options."
      />
    </div>
  </>
);

const ParamRow = ({ label, value, onChange, type = 'number', step }) => (
  <div className="sim-param-row">
    <span className="sim-param-label">{label}</span>
    <input
      className="sim-param-input"
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      step={step}
      style={type === 'text' ? { width: 160 } : {}}
    />
  </div>
);

// ===== Browse Data Tab =====
const BrowseTab = ({ browseTab, setBrowseTab, browseDate, setBrowseDate, browseData, browseLoading, onRefresh }) => {
  const browseTabs = [
    { id: 'orders', label: 'Orders' },
    { id: 'employees', label: 'Employees' },
    { id: 'menuItems', label: 'Menu Items' },
    { id: 'payments', label: 'Payments' },
  ];

  const needsDate = browseTab === 'orders' || browseTab === 'payments';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="sim-browse-tabs">
          {browseTabs.map(t => (
            <button
              key={t.id}
              className={`sim-browse-tab ${browseTab === t.id ? 'active' : ''}`}
              onClick={() => setBrowseTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div>
          {needsDate && (
            <input
              type="date"
              className="sim-date-input"
              value={browseDate}
              onChange={e => setBrowseDate(e.target.value)}
            />
          )}
          <button className="sim-btn sim-btn-secondary" onClick={onRefresh}>
            Refresh
          </button>
        </div>
      </div>

      {browseLoading ? (
        <div className="sim-empty">
          <div className="sim-spinner" style={{ borderColor: '#2563eb33', borderTopColor: '#2563eb', width: 24, height: 24 }} />
          <p>Loading...</p>
        </div>
      ) : browseData.length === 0 ? (
        <div className="sim-empty">
          <div className="sim-empty-icon">[ ]</div>
          <p>No data found. Run a simulation first.</p>
        </div>
      ) : (
        <DataTable tab={browseTab} data={browseData} />
      )}
    </>
  );
};

const DataTable = ({ tab, data }) => {
  if (tab === 'orders') return <OrdersTable data={data} />;
  if (tab === 'employees') return <EmployeesTable data={data} />;
  if (tab === 'menuItems') return <MenuItemsTable data={data} />;
  if (tab === 'payments') return <PaymentsTable data={data} />;
  return null;
};

const OrdersTable = ({ data }) => (
  <table className="sim-data-table">
    <thead>
      <tr>
        <th>Order #</th>
        <th>Opened</th>
        <th>Dining</th>
        <th>Items</th>
        <th>Total</th>
        <th>Tip</th>
        <th>Payment</th>
      </tr>
    </thead>
    <tbody>
      {data.slice(0, 50).map((order, i) => {
        const check = order.checks?.[0] || {};
        const payment = check.payments?.[0] || {};
        return (
          <tr key={order.guid || i}>
            <td>{check.displayNumber || '-'}</td>
            <td>{formatDate(order.openedDate)}</td>
            <td>{order.diningOption?.entityType === 'DiningOption' ? 'Dine In' : '-'}</td>
            <td>{check.selections?.length || 0}</td>
            <td>${(check.totalAmount || 0).toFixed(2)}</td>
            <td>${(payment.tipAmount || 0).toFixed(2)}</td>
            <td>{payment.type || '-'}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const EmployeesTable = ({ data }) => (
  <table className="sim-data-table">
    <thead>
      <tr>
        <th>Name</th>
        <th>Email</th>
        <th>Employee ID</th>
        <th>Job</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      {data.map((emp, i) => (
        <tr key={emp.guid || i}>
          <td>{emp.firstName} {emp.lastName}</td>
          <td>{emp.email}</td>
          <td>{emp.externalEmployeeId}</td>
          <td>{emp.jobReferences?.[0]?.job?.entityType || '-'}</td>
          <td>{emp.disabled ? 'Disabled' : emp.deleted ? 'Deleted' : 'Active'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MenuItemsTable = ({ data }) => (
  <table className="sim-data-table">
    <thead>
      <tr>
        <th style={{ width: 64 }}>Image</th>
        <th>Name</th>
        <th>Description</th>
        <th>Price</th>
        <th>Calories</th>
      </tr>
    </thead>
    <tbody>
      {data.map((item, i) => (
        <tr key={item.guid || i}>
          <td>
            {item.image ? (
              <img
                src={item.image}
                alt={item.name}
                className="sim-menu-item-thumb"
              />
            ) : (
              <div className="sim-menu-item-no-image">--</div>
            )}
          </td>
          <td>{item.name}</td>
          <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</td>
          <td>${(item.price || 0).toFixed(2)}</td>
          <td>{item.calories || '-'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PaymentsTable = ({ data }) => (
  <table className="sim-data-table">
    <thead>
      <tr>
        <th>GUID</th>
        <th>Type</th>
        <th>Amount</th>
        <th>Tip</th>
        <th>Total</th>
        <th>Last 4</th>
      </tr>
    </thead>
    <tbody>
      {data.slice(0, 50).map((p, i) => (
        <tr key={p.guid || i}>
          <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.guid?.substring(0, 8)}...</td>
          <td>{p.type}</td>
          <td>${(p.amount || 0).toFixed(2)}</td>
          <td>${(p.tipAmount || 0).toFixed(2)}</td>
          <td>${(p.amountTendered || 0).toFixed(2)}</td>
          <td>{p.last4Digits || '-'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ===== Integration Tab =====
const IntegrationTab = ({ simulatorUrl, onTest, testResult, onCopy, copied }) => (
  <>
    <h3 style={{ margin: '0 0 16px' }}>Simulator API URL</h3>
    <div className="sim-integration-url">
      <span>{simulatorUrl || 'Not configured - deploy stack first'}</span>
      {simulatorUrl && (
        <button className="sim-copy-btn" onClick={onCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>

    <div className="sim-instructions">
      <h4>How to Connect</h4>
      <ol>
        <li>Deploy the <code>ChulasToastSimulatorStack</code> via CDK</li>
        <li>Copy the simulator API URL above</li>
        <li>Set the environment variable on your Toast handler Lambda:
          <br /><code>TOAST_API_BASE_URL={simulatorUrl || '{simulator-url}'}</code>
        </li>
        <li>The simulator provides a dummy auth endpoint, so no credential changes needed</li>
        <li>All Toast API paths work identically (<code>/orders/v2/ordersBulk</code>, <code>/labor/v1/employees</code>, etc.)</li>
      </ol>
    </div>

    <h3 style={{ margin: '20px 0 12px' }}>Test Connection</h3>
    <button className="sim-btn sim-btn-primary" onClick={onTest}>
      Test Connection
    </button>
    {testResult && (
      <div className={`sim-test-result ${testResult.success ? 'success' : 'failure'}`}>
        {testResult.message}
      </div>
    )}
  </>
);

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

export default ToastSimulator;
